import { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, LayoutDashboard, ClipboardList, CalendarDays, MessageSquareWarning, GraduationCap, UserCircle, Menu, X, BookOpen, Video, Sparkles, Award, Rocket, FileText, PartyPopper, CalendarHeart, PanelLeftClose, PanelLeftOpen, Languages, Megaphone, Vote, Radio } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import HeaderClock, { MobileClock } from "@/components/HeaderClock";
import ThemeToggle from "@/components/ThemeToggle";
import PresenceDot from "@/components/PresenceDot";
import { SimulationBanner } from "@/components/SimulationControls";
import useIdleStatus from "@/lib/useIdleStatus";
import LiveClassBanner from "@/components/LiveClassBanner";
import UpdatesTicker from "@/components/UpdatesTicker";

const BASE_LINKS = [
  { to: "/student/dashboard", label: "Overview", icon: LayoutDashboard, testid: "snav-dashboard" },
  { to: "/student/attendance", label: "My attendance", icon: ClipboardList, testid: "snav-attendance" },
  { to: "/student/timetable", label: "Timetable", icon: CalendarDays, testid: "snav-timetable" },
  { to: "/student/homework", label: "Homework", icon: BookOpen, testid: "snav-homework" },
  { to: "/student/announcements", label: "Announcements", icon: Megaphone, testid: "snav-announcements" },
  { to: "/student/results", label: "My results", icon: Award, testid: "snav-results" },
  { to: "/student/online-classes", label: "Online classes", icon: Video, testid: "snav-online" },
  { to: "/student/live-classroom", label: "Live Classroom", icon: Radio, subtitle: "For Parent Viewing Only", testid: "snav-live-classroom" },
  { to: "/student/tuitions", label: "Tuitions", icon: Sparkles, testid: "snav-tuitions" },
  { to: "/student/polls", label: "Polls & Surveys", icon: Vote, testid: "snav-polls" },
  { to: "/student/languages", label: "Language Learning", icon: Languages, testid: "snav-languages" },
  { to: "/student/exams", label: "My Tests", icon: FileText, testid: "snav-exams" },
  { to: "/student/absence", label: "Leaves", icon: MessageSquareWarning, testid: "snav-absence" },
  { to: "/student/holidays", label: "Holidays", icon: PartyPopper, testid: "snav-holidays" },
  { to: "/student/events", label: "Events", icon: CalendarHeart, testid: "snav-events" },
  { to: "/student/profile", label: "Profile", icon: UserCircle, testid: "snav-profile" },
];

// Numeric class only — LKG/UKG return null. JEE module shows from class 5+.
function classNumber(cls) {
  if (!cls) return null;
  if (typeof cls === "number") return cls;
  const n = parseInt(String(cls), 10);
  return Number.isNaN(n) ? null : n;
}

function buildLinks(student) {
  const out = [...BASE_LINKS];
  const cn = classNumber(student?.class_name);
  if (cn != null && cn >= 5) {
    // Insert JEE just after Tuitions for natural reading order
    const idx = out.findIndex((l) => l.testid === "snav-tuitions");
    const jeeLink = { to: "/student/jee", label: "JEE Module", icon: Rocket, testid: "snav-jee" };
    if (idx >= 0) out.splice(idx + 1, 0, jeeLink); else out.push(jeeLink);
  }
  return out;
}

export default function StudentLayout({ children }) {
  const { student, logout } = useAuth();
  const presenceStatus = useIdleStatus();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("student_sidebar_collapsed") === "1");
  const toggleCollapse = () => setCollapsed((v) => {
    const n = !v;
    localStorage.setItem("student_sidebar_collapsed", n ? "1" : "0");
    return n;
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const links = buildLinks(student);

  // Heartbeat — pings backend every 60s while the tab is alive so admins/teachers
  // see this student's live presence. We always ping (even when the tab is hidden
  // or idle) so the student stays "alive", but we send `active=false` when the
  // useIdleStatus detector reports idle/hidden so the leaderboard dot flips to
  // yellow "away" instead of green "online".
  const statusRef = useRef(presenceStatus);
  statusRef.current = presenceStatus;
  useEffect(() => {
    if (!student?.id) return undefined;
    const ping = async () => {
      try {
        await api.post("/student/heartbeat", { active: statusRef.current === "online" });
      } catch { /* silent */ }
    };
    ping();
    const id = setInterval(ping, 60_000);
    const onVisibility = () => ping();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisibility); };
  }, [student?.id]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <SimulationBanner />
      <Sidebar links={links} mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} student={student} onLogout={handleLogout} collapsed={collapsed} onToggleCollapse={toggleCollapse} />

      <header className={`${collapsed ? "md:pl-16" : "md:pl-64"} sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 transition-[padding] duration-200`}>
        <div className="h-16 px-4 sm:px-6 lg:px-8 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden h-9 w-9 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center"
            data-testid="open-sidebar-btn"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Desktop: collapse/expand the sidebar */}
          <button
            type="button"
            onClick={toggleCollapse}
            className="hidden md:flex h-9 w-9 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 items-center justify-center"
            data-testid="toggle-sidebar-btn"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>

          {/* Left: live clock */}
          <div className="hidden md:flex items-center" data-testid="header-clock-slot">
            <HeaderClock />
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-sm font-medium text-slate-900 dark:text-slate-100" data-testid="header-student-name">{student?.name}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">Roll #{student?.roll_number} · {student?.class_name}-{student?.section}</div>
            </div>
            <button
              type="button"
              className="relative h-10 w-10 rounded-full overflow-visible bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 ring-2 ring-transparent hover:ring-sky-200 dark:hover:ring-sky-700 transition"
              data-testid="header-profile-pic"
              aria-label="My profile"
            >
              <span className="h-full w-full rounded-full overflow-hidden flex items-center justify-center font-semibold">
                {student?.has_photo ? (
                  <img src={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/profile/photo/${student.id}`} alt={student?.name} className="h-full w-full object-cover" />
                ) : (
                  <span>{student?.name?.split(" ").map((p) => p[0]).slice(0, 2).join("")}</span>
                )}
              </span>
              {/* Presence dot — auto-flips to "away" after 5 min of idle / tab hidden. */}
              <span
                className="absolute bottom-0 right-0 inline-flex p-0.5 rounded-full bg-white dark:bg-slate-900 shadow"
                title={presenceStatus === "online" ? "You are online" : "You are away (idle)"}
                data-testid="header-presence-dot"
              >
                <PresenceDot status={presenceStatus} size="sm" />
              </span>
            </button>
          </div>
        </div>
        <div className="md:hidden flex items-center justify-center pb-2 pt-0">
          <MobileClock />
        </div>
      </header>

      <UpdatesTicker endpoint="/student/updates/ticker" />

      <main className={`${collapsed ? "md:pl-16" : "md:pl-64"} transition-[padding] duration-200`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LiveClassBanner variant="student" displayName={student?.name} />
          {children}
        </div>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function Sidebar({ links, mobileOpen, onClose, student, onLogout, collapsed, onToggleCollapse }) {
  return (
    <>
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm"
          onClick={onClose}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 ${collapsed ? "md:w-16" : "md:w-64"} bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transform transition-all duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
        data-testid="sidebar"
        data-collapsed={collapsed ? "1" : "0"}
      >
        <div className={`h-16 flex items-center border-b border-slate-200 dark:border-slate-800 ${collapsed ? "md:px-0 md:justify-center px-5 justify-between" : "px-5 justify-between"}`}>
          <div className={`flex items-center gap-2.5 min-w-0 ${collapsed ? "md:gap-0" : ""}`}>
            <img src="/trishul-logo.png" alt="Trishul High School" className="h-9 w-9 rounded-full shrink-0" />
            <div className={`leading-tight min-w-0 ${collapsed ? "md:hidden" : ""}`}>
              <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate" style={{fontFamily:"Outfit"}} data-testid="sidebar-school-name">Trishul High School</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 truncate">Student portal · {student?.class_name}-{student?.section}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="md:hidden h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center"
            aria-label="Close menu"
            data-testid="close-sidebar-btn"
          >
            <X className="h-4 w-4" />
          </button>
          {/* Desktop collapse toggle when expanded */}
          {!collapsed && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="hidden md:flex h-8 w-8 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 items-center justify-center"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
              data-testid="sidebar-collapse-btn"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          )}
        </div>

        <nav className="px-3 py-4 space-y-1 overflow-y-auto" style={{maxHeight: "calc(100vh - 8rem)"}}>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={onClose}
              data-testid={l.testid}
              title={collapsed ? l.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${collapsed ? "md:justify-center md:px-0" : ""} ${
                  isActive
                    ? "bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300"
                    : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`
              }
            >
              <l.icon className="h-4 w-4 shrink-0" />
              <span className={collapsed ? "md:hidden" : ""}>{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 inset-x-0 px-3 py-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <button
            type="button"
            onClick={onLogout}
            data-testid="logout-btn"
            title={collapsed ? "Sign out" : undefined}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors ${collapsed ? "md:justify-center md:px-0" : ""}`}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className={collapsed ? "md:hidden" : ""}>Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
}
